import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Star, Heart } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import { useWishlist } from '../context/WishlistContext';
import { useT } from '../i18n/LanguageContext';
import { useLocalizedActivity } from '../i18n/contentLocale';

export default function ActivityCard({ activity: rawActivity }) {
  const { format } = useCurrency();
  const { isSaved, toggle } = useWishlist();
  const t = useT();
  const activity = useLocalizedActivity(rawActivity);
  const saved = isSaved(activity.id);

  return (
    <Link
      to={`/activity/${activity.id}`}
      className="group block bg-white rounded-xl overflow-hidden owa-card shadow-[0_8px_20px_-14px_rgba(11,33,61,0.18)] hover:shadow-[0_14px_34px_-16px_rgba(11,33,61,0.3)]"
    >
      <div className="relative aspect-[2/1] overflow-hidden">
        <img src={activity.image} alt={activity.title} loading="lazy" className="w-full h-full object-cover owa-card-image" />

        {/* Badges */}
        <div className="absolute top-2.5 left-2.5 flex flex-col gap-1">
          {activity.featured && (
            <span className="px-2 py-0.5 rounded-md bg-[#1fa5a3] text-white text-[11px] font-semibold uppercase tracking-wider">
              {t('card.featured')}
            </span>
          )}
          {activity.originalPrice && (
            <span className="px-2 py-0.5 rounded-md bg-[#c8a25a] text-white text-[11px] font-semibold uppercase tracking-wider">
              -{Math.round((1 - activity.price / activity.originalPrice) * 100)}%
            </span>
          )}
        </div>

        {/* Heart */}
        <button
          onClick={(e) => {
            e.preventDefault();
            toggle(activity.id);
          }}
          className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-white/90 dark:bg-slate-900/70 backdrop-blur flex items-center justify-center hover:bg-white dark:hover:bg-slate-900 transition-all"
          aria-label={saved ? t('wishlist.remove') : t('wishlist.add')}
          aria-pressed={saved}
        >
          <Heart className={`w-4 h-4 ${saved ? 'fill-[#c8a25a] text-[#c8a25a]' : 'text-[#14213d] dark:text-white'}`} />
        </button>
      </div>

      <div className="p-3.5">
        <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-1.5">
          <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">{activity.location}</span>
        </div>

        <h3 className="text-[17px] font-semibold text-[#14213d] leading-snug line-clamp-2 group-hover:text-[#1fa5a3] transition-colors">
          {activity.title}
        </h3>

        {activity.rating && activity.reviews > 0 ? (
          <div className="flex items-center gap-1.5 mt-2 text-sm">
            <Star className="w-4 h-4 fill-[#c8a25a] text-[#c8a25a]" />
            <span className="font-semibold text-[#14213d]">{activity.rating}</span>
            <span className="text-gray-500">({activity.reviews} {t('card.reviews')})</span>
          </div>
        ) : null}

        <div className="mt-2.5 pt-2.5 border-t border-gray-100 flex items-end justify-between">
          <div>
            <p className="text-[11px] uppercase text-gray-400 tracking-wide font-medium">{t('card.from')}</p>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-[#1fa5a3]">{format(activity.price)}</span>
              {activity.originalPrice && (
                <span className="text-sm text-gray-400 line-through">{format(activity.originalPrice)}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <Clock className="w-3.5 h-3.5" />
            <span>{activity.duration}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
